<script setup lang="ts">
/**
 * Per-assistant landing page — the brief intro screen between the
 * Hub grid and a chat session. Shows what the assistant is, what it
 * knows, and three sample questions the user can click to seed
 * their first query. Borrowed pattern from IndsigtFrontend's
 * assistant-onboarding splash.
 *
 * The sample questions come from `assistant.settings.samples` (the
 * operator can set them via the CLI / admin UI). When absent we
 * show a single neutral placeholder.
 *
 * Click any sample → navigate to `#/chat/<slug>` with a query-string
 * carrier so the chat shell autofocuses + pre-fills the input.
 * Click the big "Start chat →" → same destination, no pre-fill.
 */
import { computed, onMounted, ref, watch } from "vue";
import { assistantHref, hasOwnPage } from "../assistant-pages";
import { listVisibleAssistants, type Assistant } from "../api/conversations";

const props = defineProps<{ slug: string; devUser?: string }>();

const assistants = ref<Assistant[]>([]);
const loaded = ref(false);

// Reload when the dev-user picker switches identity: the list is per caller.
async function load(): Promise<void> {
  try {
    assistants.value = await listVisibleAssistants(props.devUser);
  } finally {
    loaded.value = true;
  }
}
onMounted(load);
watch(() => props.devUser, load);

const assistant = computed<Assistant | null>(() => {
  return assistants.value.find((a) => a.slug === props.slug) ?? null;
});

const samples = computed<string[]>(() => {
  const raw = assistant.value?.settings?.samples;
  if (Array.isArray(raw)) {
    return raw.filter((s): s is string => typeof s === "string" && s.length > 0).slice(0, 6);
  }
  return [];
});

function startChat(seedQuery?: string): void {
  const qs = seedQuery ? `?q=${encodeURIComponent(seedQuery)}` : "";
  window.location.hash = `#/chat/${props.slug}${qs}`;
}

function openPage(): void {
  window.location.hash = assistantHref(props.slug);
}

function back(): void {
  window.location.hash = "#/hub";
}
</script>

<template>
  <div class="landing">
    <button class="landing__back" type="button" @click="back">← Hub</button>

    <div v-if="!loaded" class="landing__loading">Loading…</div>

    <div v-else-if="!assistant" class="landing__missing">
      <h2>Assistant not found</h2>
      <p>
        The assistant <code>{{ slug }}</code> isn't visible to your
        AD groups (or doesn't exist). Try the
        <a href="#/hub">Hub</a> to see what's available.
      </p>
    </div>

    <article v-else>
      <header class="landing__header">
        <h1 class="landing__title">{{ assistant.name }}</h1>
        <p v-if="assistant.description" class="landing__desc">
          {{ assistant.description }}
        </p>
      </header>

      <section v-if="samples.length > 0" class="landing__samples">
        <h2 class="landing__samples-title">Try asking</h2>
        <div class="chips">
          <button
            v-for="(q, i) in samples"
            :key="i"
            type="button"
            class="chip"
            @click="startChat(q)"
          >
            {{ q }}
          </button>
        </div>
      </section>

      <footer class="landing__cta">
        <button v-if="hasOwnPage(slug)" class="cta" type="button" @click="openPage()">
          Åbn {{ assistant?.name ?? "siden" }} →
        </button>
        <button :class="hasOwnPage(slug) ? 'cta cta--ghost' : 'cta'" type="button" @click="startChat()">
          Start chat →
        </button>
        <!-- Contract Intelligence has a paired dashboard view —
             surface a link to it on the landing page so users
             discover the structured-overview side of the assistant. -->
        <a
          v-if="slug === 'contract-intelligence'"
          href="#/contracts/dashboard"
          class="cta cta--ghost"
        >
          Open dashboard →
        </a>
      </footer>
    </article>
  </div>
</template>

<style scoped>
.landing {
  max-width: 760px;
  margin: 0 auto;
  padding: var(--space-6) var(--space-4) var(--space-8);
}
.landing__back {
  background: none;
  border: none;
  color: var(--color-muted);
  font: inherit;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0;
  margin-bottom: var(--space-4);
}
.landing__back:hover { color: var(--color-accent); }
.landing__loading,
.landing__missing {
  padding: var(--space-6);
  text-align: center;
  color: var(--color-muted);
}
.landing__missing h2 { color: var(--color-ink); }

.landing__header { margin-bottom: var(--space-6); }
.landing__title {
  font-size: 2.25rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
  margin: 0 0 var(--space-3);
  color: var(--color-ink);
}
.landing__desc {
  font-size: 1.1rem;
  line-height: 1.55;
  color: var(--color-muted);
  margin: 0;
}

.landing__samples {
  margin: var(--space-6) 0;
}
.landing__samples-title {
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-muted);
  margin: 0 0 var(--space-3);
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}
.chip {
  background: var(--color-surface-alt);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  padding: 0.5rem 0.9rem;
  font: inherit;
  font-size: 0.9rem;
  color: var(--color-ink);
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease;
  text-align: left;
}
.chip:hover {
  background: var(--color-accent-dim);
  border-color: var(--color-accent);
}
.chip:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

.landing__cta {
  margin-top: var(--space-6);
  text-align: center;
}
.cta {
  background: var(--color-accent);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  padding: 0.85rem 2rem;
  font: inherit;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 120ms ease, transform 120ms ease;
}
.cta:hover {
  background: var(--color-accent-hover);
  transform: translateY(-1px);
}
.cta:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}
</style>
